import React, { useEffect, useState } from "react";
import { api, fileToBase64, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Briefcase, Check, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PREP = [
  { title: "Be specific", body: "Bring a one-page capability statement covering your differentiators, scale, and references." },
  { title: "Map to T+L categories", body: "Tie your offering to one of T+L's strategic interest categories below." },
  { title: "Show readiness", body: "Be prepared to discuss compliance, ESG/sustainability, and onboarding lead times." },
  { title: "Ask for the next step", body: "Always leave with a concrete follow-up — proposal, pilot, or intro to a category lead." },
];

const BRING = [
  "Branded one-pager", "Capability statement (PDF)", "Pricing template",
  "References from comparable engagements", "Compliance documentation",
];

export default function Procurement() {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [exhibitor, setExhibitor] = useState(null);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", interest_categories: [], notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/event").then(({ data }) => setEvent(data));
    if (user) api.get("/exhibitors/me").then(({ data }) => {
      setExhibitor(data.exhibitor);
      setForm((f) => ({ ...f, company_name: data.exhibitor.company_name, contact_name: data.exhibitor.primary_contact, email: data.exhibitor.email }));
    }).catch(() => {});
  }, [user]);

  const toggle = (c) => setForm((f) => ({ ...f, interest_categories: f.interest_categories.includes(c) ? f.interest_categories.filter((x) => x !== c) : [...f.interest_categories, c] }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (exhibitor) payload.exhibitor_id = exhibitor.id;
      await api.post("/procurement/meeting-request", payload);
      toast.success("Meeting request submitted. Procurement will be in touch.");
      setForm({ company_name: "", contact_name: "", email: "", interest_categories: [], notes: "" });
    } catch (e) { toast.error("Submit failed"); }
    finally { setSubmitting(false); }
  };

  const uploadCapability = async (file) => {
    if (!user) { toast.error("Sign in to upload"); return; }
    setUploading(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await api.post("/uploads", { filename: file.name, content_type: file.type, data_base64: b64, purpose: "capability" });
      if (exhibitor) await api.put("/exhibitors/me", { capability_statement_file_id: data.file_id });
      toast.success("Capability statement uploaded.");
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <Layout>
      <section className="container-summit py-12 md:py-16">
        <div className="overline mb-4"><span className="accent-rule" />Procurement Engagement</div>
        <h1 className="text-4xl sm:text-5xl">Prepare for procurement conversations.</h1>
        <p className="mt-4 max-w-2xl text-zinc-500">
          Use this guide to make the most of your direct time with Travel + Leisure procurement and sourcing leaders.
        </p>

        {/* AI Matchmaking (Signature Feature) section removed at owner request. */}

        <div className="grid lg:grid-cols-2 gap-12 mt-12">
          <div>
            <h2 className="text-2xl mb-6">How to prepare</h2>
            <div className="grid gap-4">
              {PREP.map((p) => (
                <div key={p.title} className="card-flat p-6">
                  <div className="flex items-start gap-4">
                    <Briefcase size={18} className="text-[#A39171] mt-1" strokeWidth={1.4} />
                    <div>
                      <div className="text-base font-medium">{p.title}</div>
                      <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-2xl mt-10 mb-4">What to bring</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {BRING.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-zinc-700">
                  <Check size={16} className="mt-0.5 text-black" /> {b}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-2xl mb-6">Request a meeting</h2>
            <form onSubmit={submit} className="card-flat p-6 md:p-8 grid gap-5">
              <Field label="Company"><Input required value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} className="h-12 rounded-none" /></Field>
              <Field label="Contact Name"><Input required value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} className="h-12 rounded-none" /></Field>
              <Field label="Email"><Input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="h-12 rounded-none" /></Field>
              <div>
                <Label className="overline">Interest Categories</Label>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(event?.interest_categories || []).map((c) => {
                    const on = form.interest_categories.includes(c);
                    return <button type="button" key={c} onClick={() => toggle(c)}
                      className={`pill ${on ? "bg-black text-white border-black" : "hover:border-black"}`}
                      data-testid={`interest-${c.slice(0,8)}`}>{c}</button>;
                  })}
                </div>
              </div>
              <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="rounded-none" /></Field>
              <button type="submit" disabled={submitting} className="btn-black" data-testid="procurement-submit">
                {submitting ? "Submitting…" : "Submit Meeting Request"}
              </button>
            </form>

            <h2 className="text-2xl mt-10 mb-4">Capability statement</h2>
            <div className="card-flat p-6">
              <p className="text-sm text-zinc-600">Upload your PDF so procurement can review ahead of any 1:1 meeting.</p>
              <label className="btn-outline-black mt-4 h-11 text-xs cursor-pointer" data-testid="procurement-upload-capability">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload PDF"}
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCapability(e.target.files[0])} />
              </label>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, children }) {
  return (<div><Label className="overline">{label}</Label><div className="mt-2">{children}</div></div>);
}

function Matchmaking({ event }) {
  const [profile, setProfile] = useState({ industry: "", interest_categories: [], certifications: [] });
  const [result, setResult] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("categories");

  const toggle = (k, c) => setProfile((p) => ({
    ...p, [k]: p[k].includes(c) ? p[k].filter((x) => x !== c) : [...p[k], c],
  }));

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/matchmaking/personalized", profile);
      setResult(data);
      setLoaded(true);
      setTab("categories");
    } catch (e) { toast.error("Match failed"); }
    finally { setBusy(false); }
  };

  const TABS = result ? [
    { id: "categories", label: "Category Scores", count: result.category_match_scores?.length || 0 },
    { id: "contacts", label: "Procurement Contacts", count: result.procurement_contacts?.length || 0 },
    { id: "sessions", label: "Breakouts", count: result.suggested_sessions?.length || 0 },
    { id: "exhibitors", label: "Exhibitors", count: result.exhibitor_matches?.length || 0 },
    { id: "partners", label: "Prime & Diverse Partners", count: (result.prime_partners?.length || 0) + (result.diverse_partners?.length || 0) },
  ] : [];

  return (
    <div className="mt-10 card-flat p-6 md:p-8 bg-gradient-to-b from-white to-zinc-50">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">AI Matchmaking · Signature Feature</div>
          <h2 className="serif text-3xl mt-2">Personalized recommendations.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Tell us what you&apos;re looking for and we&apos;ll surface the highest-fit categories, procurement contacts,
            breakout sessions, exhibitors, and prime/diverse partners — each with a Business Match Score.
          </p>
        </div>
        <Sparkles size={20} className="text-[#A39171] shrink-0" strokeWidth={1.4} />
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-6">
        <div>
          <Label className="overline">Industry Focus</Label>
          <select value={profile.industry} onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
            className="h-11 w-full border border-zinc-200 bg-white text-sm px-3 mt-2 uppercase tracking-[0.12em] focus:outline-none focus:border-black" data-testid="match-industry">
            <option value="">Any</option>
            {(event?.industries || []).map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="overline">Interest Categories</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(event?.interest_categories || []).map((c) => {
              const on = profile.interest_categories.includes(c);
              return <button type="button" key={c} onClick={() => toggle("interest_categories", c)}
                className={`pill ${on ? "bg-black text-white border-black" : "hover:border-black"}`}
                data-testid={`match-cat-${c.slice(0,8)}`}>{c}</button>;
            })}
          </div>
        </div>
        <div className="md:col-span-3">
          <Label className="overline">Looking for these certifications</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(event?.certifications || []).map((c) => {
              const on = profile.certifications.includes(c);
              return <button type="button" key={c} onClick={() => toggle("certifications", c)}
                className={`pill ${on ? "bg-black text-white border-black" : "hover:border-black"}`}
                data-testid={`match-cert-${c}`}>{c}</button>;
            })}
          </div>
        </div>
      </div>

      <button onClick={run} disabled={busy} className="btn-black mt-6" data-testid="match-run">
        <Sparkles size={14} /> {busy ? "Matching…" : "Run AI Matchmaking"}
      </button>

      {loaded && result && (
        <div className="mt-10">
          {/* Tabs */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 border-b border-zinc-200">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`match-tab-${t.id}`}
                className={`pb-3 text-xs uppercase tracking-[0.22em] border-b-2 transition-colors ${
                  tab === t.id ? "border-black text-black" : "border-transparent text-zinc-500 hover:text-black"
                }`}>
                {t.label} <span className="ml-1 tabular-nums text-zinc-400">{t.count}</span>
              </button>
            ))}
          </div>

          {tab === "categories" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))] mt-6">
              {(result.category_match_scores || []).map((c) => (
                <Link key={c.slug} to={`/opportunities/${c.slug}`} className="bg-white p-5 hover:bg-zinc-50 transition-colors flex items-center justify-between gap-4" data-testid={`match-score-${c.slug}`}>
                  <div>
                    <div className="serif text-xl">{c.name}</div>
                    <div className="overline mt-1">Business Match Score</div>
                  </div>
                  <ScoreRing v={c.score} />
                </Link>
              ))}
            </div>
          )}

          {tab === "contacts" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))] mt-6">
              {(result.procurement_contacts || []).length === 0 && <div className="bg-white p-6 col-span-full text-sm text-zinc-500">No procurement contacts matched. Try adjusting your interest categories.</div>}
              {(result.procurement_contacts || []).map((sp) => (
                <div key={sp.id} className="bg-white p-5">
                  <div className="overline">{sp.org}</div>
                  <div className="serif text-xl mt-1">{sp.name}</div>
                  <div className="text-sm text-zinc-600 mt-2">{sp.title}</div>
                  {sp.session_title && <div className="overline mt-3 text-zinc-400">Speaks at · {sp.session_title}</div>}
                </div>
              ))}
            </div>
          )}

          {tab === "sessions" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))] mt-6">
              {(result.suggested_sessions || []).map((s) => (
                <Link key={s.id} to="/schedule" className="bg-white p-5 hover:bg-zinc-50 transition-colors">
                  <div className="overline">{s.topic || "Breakout"}</div>
                  <div className="serif text-lg mt-2">{s.title}</div>
                  <div className="text-xs text-zinc-500 mt-2">{s.location || ""}</div>
                </Link>
              ))}
            </div>
          )}

          {tab === "exhibitors" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))] mt-6">
              {(result.exhibitor_matches || []).map((m) => (
                <Link key={m.id} to={`/directory/${m.id}`} className="bg-white p-5 flex flex-col gap-3 hover:bg-zinc-50 transition-colors" data-testid={`match-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="serif text-lg">{m.company_name}</div>
                      <div className="overline mt-1">{m.industry}</div>
                    </div>
                    <ScoreRing v={m.match_score} />
                  </div>
                  {m.certifications?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {m.certifications.slice(0, 3).map((c) => <span key={c} className="pill text-[10px]">{c}</span>)}
                    </div>
                  )}
                  <p className="text-sm text-zinc-600 line-clamp-2">{m.description}</p>
                </Link>
              ))}
            </div>
          )}

          {tab === "partners" && (
            <div className="mt-6 grid gap-6">
              <PartnerStrip title="Potential Prime Supplier Partners" items={result.prime_partners} />
              <PartnerStrip title="Potential Diverse Supplier Partners" items={result.diverse_partners} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PartnerStrip({ title, items }) {
  return (
    <div>
      <div className="overline mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))]">
        {(items || []).length === 0 && <div className="bg-white p-5 col-span-full text-sm text-zinc-500">No matches yet.</div>}
        {(items || []).map((p) => (
          <Link key={p.id} to={`/directory/${p.id}`} className="bg-white p-5 hover:bg-zinc-50 transition-colors flex flex-col gap-2">
            <div className="serif text-lg">{p.company_name}</div>
            <div className="overline">{p.industry}</div>
            {p.certifications?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {p.certifications.slice(0, 4).map((c) => <span key={c} className="pill text-[10px]">{c}</span>)}
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

function ScoreRing({ v }) {
  const pct = Math.max(0, Math.min(100, v || 0));
  const r = 22, c = 2 * Math.PI * r;
  const off = c * (1 - pct / 100);
  return (
    <div className="relative h-14 w-14 shrink-0">
      <svg className="rotate-[-90deg]" viewBox="0 0 60 60" width="56" height="56">
        <circle cx="30" cy="30" r={r} stroke="#e5e5e5" strokeWidth="4" fill="none" />
        <circle cx="30" cy="30" r={r} stroke="#0A0A0A" strokeWidth="4" fill="none"
                strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-medium">{pct}%</div>
    </div>
  );
}
